import React from 'react';
import { Link } from 'react-router-dom';
import { Sprout, Heart, MapPin, Quote, ArrowUpRight } from 'lucide-react';
import { AlmondIcon } from '../components/AlmondIcon';
import useReveal from '../hooks/useReveal';
import { about, images, secretRecipe } from '../mock/mock';
import { useContent, resolveImg } from '../context/ContentContext';

const ICONS = { Leaf: AlmondIcon, Sprout, Heart, MapPin };

export default function About() {
  useReveal([]);
  const { img, content } = useContent();
  const a = content.about || {};
  const values = a.values && a.values.length ? a.values : about.values;
  const steps = a.steps && a.steps.length ? a.steps : secretRecipe.steps;

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      {/* hero, hidden; remove the spacer above and "hidden" below to reinstate */}
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center bg-[#E9E7E2] text-[#2C3A28] overflow-hidden">
        <img
          src={about.headerImage}
          alt="Marialuisa with fresh Al Naturale almond milk"
          className="absolute inset-0 w-full h-full object-cover object-[25%_28%] opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            Our story
          </span>
          <h1 className="mt-4 text-6xl sm:text-7xl max-w-3xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
            The Al Naturale Promise
          </h1>
            <p className="mt-6 max-w-2xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
              {about.intro}
            </p>
        </div>
      </section>

      {/* OUR NUT MILKS */}
      <section className="py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="reveal">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] uppercase tracking-tight">
              {a.nutMilksHeading || 'Our Nut Milks'}
            </h2>
            <p className="mt-6 text-[#2C3A28] text-lg leading-relaxed">
              {a.nutMilksPara1 || 'Al Naturale was born to offer the finest plant-based milks in Melbourne, providing a genuinely natural taste without any artificial additives or preservatives. Our mission is to serve those who are mindful of their dietary choices and seek authentic flavour and true enjoyment in their alternative milk options.'}
            </p>
            <p className="mt-5 text-[#2C3A28] text-lg leading-relaxed">
              {a.nutMilksPara2 || 'We are committed to delivering the highest quality plant-based milks by making sustainable and ethical choices, such as packaging each milk in eco-friendly glass bottles. Our approach not only supports the environment but also nourishes your body, recognising the incredible work it does every day.'}
            </p>
          </div>
          <div className="reveal">
            <img
              src={resolveImg(a.nutMilksImage) || 'https://customer-assets-0z36b82j.emergentagent.net/job_fresh-italiano/artifacts/vpublyab_image.png'}
              alt="Al Naturale nut milk range with almonds"
              className="w-full rounded-[2rem] object-cover shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* About our Founder */}
      <section className="pb-24 sm:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-0 items-stretch rounded-[2rem] overflow-hidden shadow-xl bg-[#E2E4E6]">
          <div
            className="relative h-[420px] lg:h-auto bg-cover bg-no-repeat -mt-px -mb-1"
            style={{ backgroundImage: `url(${resolveImg(a.founderImage) || about.founderImage})`, backgroundPosition: 'center 25%' }}
            role="img"
            aria-label="Marialuisa, founder of Al Naturale"
          />
          <div className="relative px-8 sm:px-14 py-14">
            <h2 className="reveal font-display text-4xl sm:text-5xl font-semibold text-[#2C3A28] text-center uppercase">
              {a.founderHeading || 'About our Founder'}
            </h2>
            <div className="reveal mt-8 space-y-5 text-center">
              <p className="text-[#2C3A28] text-lg leading-relaxed">
                {a.founderQuote1 || '“Coming from a little town called Monopoli in the south of Italy (Puglia), I was always surrounded by the gifts of mother nature. Crystal clear ocean, a sun that warms up the soul, majestic century’s trees, fresh local produce, the smell of the food as you are walking by, and this is how you will end up having the best moments of your life that are so memorable and alive within you, no matter how many years pass by”'}
              </p>
              <p className="text-[#2C3A28] text-lg leading-relaxed">
                {a.founderQuote2 || 'Marialuisa believes in the value of eating real food and being present. She says that these important cornerstones are what shapes our relationship with food. Give yourself the best gift you can, time. Take the time to enjoy what you truly love.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* quote */}
      <section className="pb-8">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center reveal">
          <Quote size={40} className="mx-auto text-[#2C3A28]" />
          <blockquote className="mt-6 font-display italic text-3xl sm:text-4xl leading-snug text-[#2C3A28]">
            “{a.quote || about.quote}”
          </blockquote>
          <p className="mt-5 text-[#3B2A20]/60 uppercase tracking-[0.18em] text-sm">
            {a.quoteBy || about.quoteBy}
          </p>
        </div>
      </section>

      {/* values */}
      <section className="py-16 bg-[#2C3A28] text-[#EDEAE0] grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
              {a.valuesEyebrow || 'What we stand for'}
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">
              {a.valuesHeading || 'Our promise in every bottle'}
            </h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => {
              const Icon = ICONS[v.icon] || AlmondIcon;
              return (
                <div
                  key={v.title}
                  className="reveal rounded-2xl bg-[#EDEAE0]/6 border border-[#EDEAE0]/12 p-7 hover:bg-[#EDEAE0]/10 transition-colors"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#2C3A28] text-[#E9E5D8] flex items-center justify-center">
                    <Icon size={22} />
                  </div>
                  <p className="mt-5 font-display text-2xl font-bold text-[#E9E5D8]">{v.title}</p>
                  <p className="mt-2 text-[#E9E5D8] leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* recipe / craft */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <img src={resolveImg(a.recipeImage) || img('pour', images.pour)} alt="Pouring fresh nut milk" className="rounded-[2rem] w-full h-[520px] object-cover shadow-xl" />
          </div>
          <div className="reveal">
            <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
              {a.recipeEyebrow || "How it's made"}
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28]">
              {a.recipeHeading || secretRecipe.title}
            </h2>
            <p className="mt-6 text-[#2C3A28] text-lg leading-relaxed">{a.recipeBody || secretRecipe.body}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {steps.map((s) => (
                <div key={s.n} className="rounded-2xl bg-[#DCDFE2] p-5">
                  <span className="font-display text-2xl text-[#2C3A28]">{s.n}</span>
                  <p className="mt-1 font-display text-xl text-[#2C3A28]">{s.t}</p>
                  <p className="mt-1 text-sm text-[#2C3A28]/80 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal rounded-[2.5rem] bg-[#2C3A28] text-white px-8 sm:px-16 py-16 text-center relative overflow-hidden grain">
            <h2 className="relative font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">
              {a.ctaHeading || 'Come and taste the story'}
            </h2>
            <p className="relative mt-4 max-w-xl mx-auto text-white/80 text-lg">
              {a.ctaBody || 'Visit us on Acland Street, or discover our range and bring the Al Naturale experience home.'}
            </p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/cafe"
                className="inline-flex items-center gap-2 rounded-full bg-[#EDEAE0] text-[#2C3A28] px-8 py-4 font-semibold hover:bg-white transition-colors"
              >
                {a.ctaBtn1 || 'Visit the cafe'}
              </Link>
              <Link
                to="/products"
                className="group inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-8 py-4 font-semibold hover:bg-white/10 transition-colors"
              >
                {a.ctaBtn2 || 'Shop the range'}
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
